import {
  DEFAULT_SEEK_STEP_SEC,
  SEEK_ANIMATION,
} from '|>/shared/constants/rewind'
import { Events } from '|>/shared/events'
import { div } from '|>/shared/h'
import { on, register } from '|>/shared/vjs'
import { BaseComponent } from '../base'

import './tap-handler.css'

// delay between single and double tap
const DOUBLE_TAP_DELAY = 300

const Direction = {
  Left: -1,
  Right: 1,
} as const
type Direction = (typeof Direction)[keyof typeof Direction]

/**
 * TapHandler component
 */
@register
export class TapHandler extends BaseComponent {
  // current amount of sec to seek
  sec: number | undefined

  // current direction
  direction: Direction | undefined

  actStateBefore: undefined | boolean

  @on(Events.Media.SeekStep, (step?: number) => step || DEFAULT_SEEK_STEP_SEC)
  seekStep: number = DEFAULT_SEEK_STEP_SEC

  @on(Events.Media.ProgressBarChanged)
  progressBar: Events.Media.ProgressBarChanged = undefined

  declare doubleTapInActionId?: ReturnType<typeof this.setTimeout>

  // timeout ids for labels and single tap
  private tapTimeoutId: number | undefined

  constructor(player, options) {
    super(player, options)

    /* Handlers from this component is used by reference by rewind buttons and hotkeys */
    ;(player as any).tapHandler = this

    player.el_.addEventListener('touchend', this.handleTouchEnd.bind(this))
    player.el_.addEventListener('touchstart', this.handleTouchStart.bind(this))
  }

  animateSeek(direction: number) {
    /*
     * Plays animation seek right/left
     */

    /* doubleTapInActionId existence means that animation is on the screen */
    if (this.doubleTapInActionId) this.clearTimeout(this.doubleTapInActionId)
    this.doubleTapInActionId = this.setTimeout(() => {
      this.doubleTapInActionId = undefined
      this.player_.removeClass('x-animate-seek-left')
      this.player_.removeClass('x-animate-seek-right')
    }, 600)

    this.player_.removeClass('x-animate-seek-left')
    this.player_.removeClass('x-animate-seek-right')

    if (direction > 0)
      this.requestAnimationFrame(() =>
        this.player_.addClass('x-animate-seek-right')
      )
    else if (direction < 0)
      this.requestAnimationFrame(() =>
        this.player_.addClass('x-animate-seek-left')
      )
  }

  handleTouchStart() {
    /* For hiding / setting user to inactive state when tapped
     * goal of this handlers - get user state BEFORE videoJs sets it true.
     * coz if you get value after vjs sets it to true, interface immediately closes
     */
    this.actStateBefore = this.player_.userActive()
  }

  override createEl() {
    return div('.vjs-tap')
  }

  isTargetable(target: HTMLElement) {
    if (!target) return

    return (
      target.tagName === 'VIDEO' ||
      target.matches('.vjs-x-middle-bunk') ||
      target.matches('.video-js')
    )
  }

  handleTouchEnd(event) {
    /* player tap handler */

    if (this.tapTimeoutId) {
      // double tap case
      // do not track double taps, on not expected elements
      if (!this.isTargetable(event.target)) return
      /* TODO maybe better track all double tap and filter out vjs-control tap? */

      this.clearTimeout(this.tapTimeoutId)
      this.tapTimeoutId = undefined

      const touchX = event.changedTouches[0].clientX
      const playerEl = this.player().el() as HTMLElement
      const playerWidth = playerEl.offsetWidth

      this.handleDoubleTap(
        touchX < playerWidth / 2 // to the left or right of the player
          ? Direction.Left
          : Direction.Right
      )
    } else {
      // single case
      this.tapTimeoutId = this.setTimeout(() => {
        this.handleSingleTap(event)
        this.tapTimeoutId = undefined
      }, DOUBLE_TAP_DELAY)
    }
  }

  handleSingleTap(event) {
    // Ignore clicks on control buttons
    if (event.target.closest('.vjs-control')) return

    // Ignore double-tap last click, that may be odd, and be part of double-tap
    if (this.doubleTapInActionId) return

    if (this.actStateBefore) this.player_.userActive(false)
  }

  handleDoubleTap(direction: Direction) {
    const shiftSec = direction * this.seekStep

    if (this.progressBar === undefined)
      this.player_.vodSeekBar.trySeek({
        shiftSec,
        animation: SEEK_ANIMATION.TAP,
      })
    else
      this.player_.liveSeekBar.trySeek({
        shiftSec,
        animation: SEEK_ANIMATION.TAP,
      })
  }
}

export default TapHandler
